/**
 * PlantillaV2 - Employee roster management for company administrators.
 * Design from Lovable, logic from chirigota.
 */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
	Users,
	UserCheck,
	Clock,
	UserX,
	Search,
	Send,
	Download,
	Mail,
	RotateCcw,
	ChevronRight,
	ChevronLeft,
	TrendingUp,
	MoreHorizontal,
	DollarSign,
	CheckCircle2,
	XCircle,
	Edit3,
	UserMinus,
	Building2,
	Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { PageHeader } from '../../../components/ui/page-header';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '../../../components/ui/select';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { formatArcaNumber } from '../../../utils/formatArcaNumbers';
import getUsersByCompanyId from '../../../api/users/getUsersByCompanyId';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import findParticipantsContributionData from '../../../api/calculators/findParticipantsContributionData';
import getCollectiveAgreementGroups from '../../../api/companies/getCollectiveAgreementGroups';
import getEmployeeInviteRequirements from '../../../api/companies/getEmployeeInviteRequirements';
import inviteEmployeesMinimal, {
	InviteEmployeeMinimal
} from '../../../api/companies/inviteEmployeesMinimal';
import reinviteEmployee from '../../../api/companies/reinviteEmployee';
import exportStaffTable from '../../../api/emails/exportStaffTable';
import InviteEmployeesMinimalModalV2 from '../../../components/Modals/InviteEmployeesMinimalModalV2';
import EmployeeLeaveModalV2 from '../../../components/Modals/EmployeeLeaveModalV2';
import EditSalaryModalV2 from '../../../components/Modals/EditSalaryModalV2';
import BulkSalaryReviewModalV2 from '../../../components/Modals/BulkSalaryReviewModalV2';
import { useToast } from '../../../components/ui/use-toast';
import { UserI, UserEmploymentStatus } from '../../../api/users/interfaces/Users';
import { CompanyWithEppConfigI } from '../../../api/companies/interfaces/CompanyInfo';

type EmployeeStatus = 'active' | 'pending' | 'declined' | 'inactive';

/**
 * View model that combines UserI data with contribution data from
 * the participants-contributions endpoint. Not a backend interface.
 */
interface Employee {
	id: string;
	email: string;
	name: string;
	firstName: string | null;
	lastName: string | null;
	nif: string | null;
	status: EmployeeStatus;
	joinDate: string | null;
	monthlyContribution: number;
	companyContribution: number;
	totalSaved: number;
	lastInvitedAt: string | null;
	salary: number | null;
	userId: string | null;
	companyId: string | null;
	leaveDate: string | null;
	// Contacto personal: el modal de baja lo necesita para saber si pedirlo
	personalEmail: string | null;
	phone: string | null;
}

const STATUS_CONFIG: Record<EmployeeStatus, { labelKey: string; className: string }> = {
	active: {
		labelKey: 'admin.staff.statusActive',
		className: 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/15'
	},
	pending: {
		labelKey: 'admin.staff.statusPending',
		className: 'bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/15'
	},
	declined: {
		labelKey: 'admin.staff.statusDeclined',
		className: 'bg-secondary/10 text-secondary border-secondary/20 hover:bg-secondary/15'
	},
	inactive: {
		labelKey: 'admin.staff.statusInactive',
		className: 'bg-gray-500/10 text-gray-600 border-gray-500/20 hover:bg-gray-500/15'
	}
};

const ITEMS_PER_PAGE = 10;

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.58, 1] as const }
	})
};

const PlantillaV2: React.FC = () => {
	const { token, companyId, userId, isMultiCompany } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { toast } = useToast();

	// Data states
	const [isLoading, setIsLoading] = useState(true);
	const [employees, setEmployees] = useState<Employee[]>([]);
	const [allCompanies, setAllCompanies] = useState<Array<{ id: string; name: string }>>([]);
	const [isHolding, setIsHolding] = useState(false);

	// Filter states
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState<'all' | EmployeeStatus>('all');
	const [salaryFilter, setSalaryFilter] = useState<'all' | 'informed' | 'missing'>('all');
	const [companyFilter, setCompanyFilter] = useState<'all' | string>('all');
	const [currentPage, setCurrentPage] = useState(1);

	// Modal states
	const [inviteModalOpen, setInviteModalOpen] = useState(false);
	const [inviteRequirements, setInviteRequirements] = useState<any>(null);
	const [inviteRequirementsLoading, setInviteRequirementsLoading] = useState(false);
	const [collectiveAgreementGroups, setCollectiveAgreementGroups] = useState<any[]>([]);
	const [isInviting, setIsInviting] = useState(false);
	// For holdings (multi-company) the admin must pick WHICH company to invite to.
	// Single-company admins invite to their own company (companyId from context).
	const [selectedInviteCompanyId, setSelectedInviteCompanyId] = useState('');

	// Target company for the invite flow (requirements, groups, and the invite call).
	const inviteCompanyId = isMultiCompany ? selectedInviteCompanyId : companyId;

	useEffect(() => {
		// Multi-company: force an explicit selection. Single-company: default to own company.
		setSelectedInviteCompanyId(isMultiCompany ? '' : companyId || '');
	}, [isMultiCompany, companyId]);

	const [isExporting, setIsExporting] = useState(false);
	const [bulkSalaryOpen, setBulkSalaryOpen] = useState(false);
	const [salaryDetailEmployee, setSalaryDetailEmployee] = useState<Employee | null>(null);
	const [terminationEmployee, setTerminationEmployee] = useState<Employee | null>(null);

	// Fetch employees
	const fetchEmployees = useCallback(async () => {
		if (!token || !companyId) return;
		setIsLoading(true);

		try {
			const currentYear = new Date().getFullYear();
			const [usersRes, participantsRes, configRes] = await Promise.all([
				// El endpoint /users/{companyId} ya incluye plantilla actual + bajas (TERMINATED)
				// por defecto (backend default-include). La lista llega con inactivos incluidos.
				getUsersByCompanyId(token, companyId),
				findParticipantsContributionData(token, 1, 500, currentYear, null, null, null),
				getCompanyInfoEppConfig(token, companyId)
			]);

			// Build contribution map from participants-contributions endpoint
			interface ParticipantItem {
				user?: { id?: string; name?: string; email?: string };
				userId?: string;
				nextParticipantContribution?: number;
				nextPromoterContribution?: number;
				totalEconomicsRightsGenerated?: number;
			}
			const contribMap = new Map<string, ParticipantItem>();
			if (participantsRes?.data?.items) {
				participantsRes.data.items.forEach((item: ParticipantItem) => {
					const userId = item.user?.id || item.userId;
					if (userId) contribMap.set(userId, item);
				});
			}

			if (usersRes.success && usersRes.data) {
				const formattedEmployees: Employee[] = (usersRes.data as UserI[]).map((user) => {
					const uid = String(user.id);
					const contrib = contribMap.get(uid);

					// Criterio de baja PROD-verificado: employmentStatus === TERMINATED (check positivo,
					// NO `!== CURRENTLY_EMPLOYED`: eso colaría autónomos INDIVIDUAL y users sin estado).
					// Va PRIMERO: una baja es inactivo aunque tuviera noWill o perfil completo.
					let status: EmployeeStatus;
					if (user.employmentStatus === UserEmploymentStatus.TERMINATED) {
						status = 'inactive';
					} else if (user.noWill) {
						status = 'declined';
					} else if (user.completedProfile || user.completedOnboarding) {
						status = 'active';
					} else {
						status = 'pending';
					}

					return {
						id: uid,
						email: user.email,
						name:
							user.name ||
							`${user.firstSurname || ''} ${user.lastSurname || ''}`.trim() ||
							user.email,
						firstName: user.firstSurname || null,
						lastName: user.lastSurname || null,
						nif: user.nif || null,
						status,
						joinDate: user.profileCompletedAt || user.onboardingCompletedAt || null,
						monthlyContribution: contrib?.nextParticipantContribution || 0,
						companyContribution: contrib?.nextPromoterContribution || 0,
						totalSaved: contrib?.totalEconomicsRightsGenerated || 0,
						lastInvitedAt: user.createdAt || null,
						salary: user.grossAnnualSalary || null,
						userId: uid,
						companyId: user.companyId || companyId,
						leaveDate: user.companyLeaveDate || null,
						personalEmail: user.personalEmail || null,
						phone: user.phone || null
					};
				});
				setEmployees(formattedEmployees);
			}

			if (configRes.success && configRes.data) {
				const companies = configRes.data.allCompanies || [];
				setAllCompanies(companies.map((c: CompanyWithEppConfigI) => ({ id: c.id, name: c.name })));
				setIsHolding(companies.length > 1);
			}
			// Collective agreement groups are loaded per target company when the invite
			// modal opens (see the requirements effect), so a holding admin sees the
			// groups of the company they actually selected to invite to.
		} catch (error) {
			console.error('Error fetching employees:', error);
			toast({
				variant: 'destructive',
				title: 'Error',
				description: translate('admin.staff.errorLoadingEmployees')
			});
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, companyId, toast]);

	useEffect(() => {
		fetchEmployees();
	}, [fetchEmployees]);

	// Fetch invite requirements + collective agreement groups for the TARGET company.
	// Keyed on inviteCompanyId so switching company in a holding refreshes both
	// (required fields and the convenio groups that populate the group selector).
	useEffect(() => {
		// Clear requirements/groups from a previously selected company so a holding
		// admin never sees stale required fields or convenio groups while switching
		// SLs (or if the next fetch fails partway through).
		setInviteRequirements(null);
		setCollectiveAgreementGroups([]);

		const fetchRequirements = async () => {
			if (!inviteModalOpen || !token || !inviteCompanyId) return;
			setInviteRequirementsLoading(true);
			try {
				const res = await getEmployeeInviteRequirements(token, inviteCompanyId);
				if (res.success && res.data) {
					setInviteRequirements(res.data);
				}
				const groupsRes = await getCollectiveAgreementGroups(token, inviteCompanyId);
				setCollectiveAgreementGroups(groupsRes?.groups?.length ? groupsRes.groups : []);
			} catch (error) {
				console.error('Error fetching invite requirements:', error);
			} finally {
				setInviteRequirementsLoading(false);
			}
		};
		fetchRequirements();
	}, [inviteModalOpen, token, inviteCompanyId]);

	// Filtered employees
	const filtered = useMemo(() => {
		return employees
			.filter((emp) => {
				const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
				const matchesSearch =
					search === '' ||
					emp.email.toLowerCase().includes(search.toLowerCase()) ||
					emp.name.toLowerCase().includes(search.toLowerCase()) ||
					(emp.nif && emp.nif.toLowerCase().includes(search.toLowerCase()));
				const matchesSalary =
					salaryFilter === 'all' ||
					(salaryFilter === 'informed' && emp.status === 'active' && emp.salary != null) ||
					(salaryFilter === 'missing' && emp.status === 'active' && emp.salary == null);
				const matchesCompany = companyFilter === 'all' || emp.companyId === companyFilter;
				return matchesStatus && matchesSearch && matchesSalary && matchesCompany;
			})
			.sort((a, b) => {
				// Inactivos (bajas) siempre al final del listado
				const statusOrder = { active: 0, pending: 1, declined: 2, inactive: 3 };
				const statusDiff = statusOrder[a.status] - statusOrder[b.status];
				if (statusDiff !== 0) return statusDiff;
				// Dentro de inactivos: por fecha de baja, el más reciente al final
				if (a.status === 'inactive') {
					return (a.leaveDate || '').localeCompare(b.leaveDate || '');
				}
				const dateA = a.joinDate || a.lastInvitedAt || '';
				const dateB = b.joinDate || b.lastInvitedAt || '';
				return dateB.localeCompare(dateA);
			});
	}, [employees, statusFilter, search, salaryFilter, companyFilter]);

	// Pagination
	const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
	const safePage = Math.min(currentPage, totalPages);
	const paginatedItems = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

	// Counts — total y tasa de adopción miden solo plantilla actual (excluye bajas)
	const total = employees.filter((e) => e.status !== 'inactive').length;
	const active = employees.filter((e) => e.status === 'active').length;
	const pending = employees.filter((e) => e.status === 'pending').length;
	const declined = employees.filter((e) => e.status === 'declined').length;
	const adoptionRate = total > 0 ? Math.round((active / total) * 100) : 0;

	// Handlers
	const handleInvite = async (employeesToInvite: InviteEmployeeMinimal[]) => {
		if (!token) return;
		// Multi-company guard with explicit feedback (the V2 regression returned silently).
		if (!inviteCompanyId) {
			toast({
				variant: 'destructive',
				title: translate('admin.staff.inviteEmployeesToastTitle'),
				description: translate('admin.staff.selectCompanyFirst')
			});
			return;
		}
		setIsInviting(true);
		try {
			const res = await inviteEmployeesMinimal(token, inviteCompanyId, employeesToInvite);
			if (res.success) {
				const result = res.data;
				const reactivated = result?.totalEmployeesReactivated || 0;
				const created = result?.totalEmployeesCreated || 0;
				const existed = result?.totalEmployeesExisted || 0;

				let description: string;
				if (reactivated > 0 && created === 0) {
					description = `${reactivated} ${reactivated === 1 ? translate('admin.staff.employeeReactivatedSingular') : translate('admin.staff.employeeReactivatedPlural')}`;
				} else if (reactivated > 0 && created > 0) {
					description = `${created} ${translate('admin.staff.inviteEmployeesToastPlural')} ${translate('admin.staff.inviteEmployeesSendedPlural')}, ${reactivated} ${reactivated === 1 ? translate('admin.staff.employeeReactivatedSingular') : translate('admin.staff.employeeReactivatedPlural')}`;
				} else if (existed > 0 && created === 0) {
					description = translate('admin.staff.inviteEmployeesAlreadyExist');
				} else {
					description = `${employeesToInvite.length} ${translate('admin.staff.inviteEmployeesToastPlural')} ${translate('admin.staff.inviteEmployeesSendedPlural')}`;
				}

				toast({
					title: translate('admin.staff.inviteEmployeesCompleted'),
					description
				});
				setInviteModalOpen(false);
				fetchEmployees();
			} else {
				throw new Error(res.message || 'Error inviting employees');
			}
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: translate('admin.staff.inviteEmployeesToastTitle'),
				description:
					error instanceof Error
						? error.message
						: translate('admin.staff.inviteEmployeesToastDescription')
			});
		} finally {
			setIsInviting(false);
		}
	};

	const handleExport = async () => {
		if (!token) return;
		setIsExporting(true);
		try {
			const currentYear = new Date().getFullYear();
			const res = await exportStaffTable(token, currentYear);
			if (res.success) {
				toast({
					title: translate('admin.staff.exportDataSuccessTitle'),
					description: translate('admin.staff.exportDataSuccessDescription')
				});
			} else {
				toast({
					variant: 'destructive',
					title: translate('admin.staff.exportDataErrorTitle'),
					description: translate('admin.staff.exportDataErrorDescription')
				});
			}
		} catch (error) {
			toast({
				variant: 'destructive',
				title: translate('admin.staff.exportDataErrorTitle'),
				description: translate('admin.staff.exportDataErrorDescription')
			});
		} finally {
			setIsExporting(false);
		}
	};

	// Reinvitar = re-enviar invitación (pending) o re-incorporar (declined, el backend
	// revierte el rechazo). Solo necesita el userId; el endpoint es company-scoped.
	const handleReinvite = async (emp: Employee) => {
		if (!token || !emp.companyId || !emp.userId) return;
		try {
			const res = await reinviteEmployee(token, emp.companyId, emp.userId);
			if (res.success) {
				toast({
					title: translate('admin.staff.invitationResent'),
					description: `${emp.email} ${translate('admin.staff.invitationResentDescription')}`
				});
				fetchEmployees();
			} else {
				throw new Error(res.message || 'Error');
			}
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: translate('admin.staff.inviteEmployeesToastTitle'),
				description:
					error instanceof Error
						? error.message
						: translate('admin.staff.inviteEmployeesToastDescription')
			});
		}
	};

	const handleBulkReinvite = async () => {
		if (!token) return;
		const reinvitable = employees.filter(
			(e) => (e.status === 'pending' || e.status === 'declined') && e.companyId && e.userId
		);
		if (reinvitable.length === 0) return;
		try {
			await Promise.all(
				reinvitable.map((emp) => reinviteEmployee(token, emp.companyId!, emp.userId!))
			);
			toast({
				title: translate('admin.staff.inviteEmployeesCompleted'),
				description: `${reinvitable.length} ${translate('admin.staff.bulkReinviteSuccess')}`
			});
			fetchEmployees();
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: translate('admin.staff.inviteEmployeesToastTitle'),
				description:
					error instanceof Error
						? error.message
						: translate('admin.staff.inviteEmployeesToastDescription')
			});
		}
	};

	const formatDate = (d: string | null) => {
		if (!d) return '—';
		return new Date(d).toLocaleDateString('es-ES', {
			day: 'numeric',
			month: 'short',
			year: 'numeric'
		});
	};

	const getCompanyName = (companyIdVal: string | null) => {
		if (!companyIdVal) return '';
		const company = allCompanies.find((c) => c.id === companyIdVal);
		return company?.name || '';
	};

	// Render action menu
	const renderActionMenu = (emp: Employee) => (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="h-8 w-8">
					<MoreHorizontal className="w-4 h-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-52">
				{emp.status === 'active' && (
					<>
						<DropdownMenuItem
							className="gap-2 cursor-pointer"
							onClick={() => setSalaryDetailEmployee(emp)}
						>
							<Edit3 className="w-4 h-4" />
							{emp.salary != null
								? translate('admin.staff.editSalary')
								: translate('admin.staff.informSalary')}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="gap-2 cursor-pointer text-destructive focus:text-destructive"
							onClick={() => setTerminationEmployee(emp)}
						>
							<UserMinus className="w-4 h-4" />
							{translate('admin.staff.terminate')}
						</DropdownMenuItem>
					</>
				)}
				{(emp.status === 'pending' || emp.status === 'declined') && (
					<DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => handleReinvite(emp)}>
						<RotateCcw className="w-4 h-4" />
						{translate('admin.staff.reinvite')}
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<motion.div initial="hidden" animate="visible" className="space-y-6">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. Antes el `h1` iba a
			    `text-2xl sm:text-3xl font-bold`: sin `font-heading` y por debajo del canon.
			    Los botones pasan a `action`, así que la fila flex la monta el componente — y de paso
			    se apilan en móvil, que antes no pasaba hasta `sm`.
			    `width="full"` porque esta pantalla NO tenía contenedor y va a todo el ancho: darle el
			    ancho por defecto estrecharía la tabla de plantilla (decisión de Marisa, 29/09/26). */}
			<motion.div custom={0} variants={fadeUp}>
				<PageHeader
					width="full"
					title={translate('admin.dashboard.staff')}
					subtitle={translate('admin.staff.description')}
					action={
						<div className="flex items-center gap-2 flex-wrap">
							<Button
								variant="outline"
								size="sm"
								className="gap-2"
								onClick={() => setBulkSalaryOpen(true)}
							>
								<DollarSign className="w-4 h-4" />
								<span className="hidden sm:inline">
									{translate('admin.bulkSalary.updateButton')}
								</span>
							</Button>
							<Button
								variant="outline"
								size="sm"
								className="gap-2"
								onClick={handleExport}
								disabled={isExporting}
							>
								{isExporting ? (
									<Loader2 className="w-4 h-4 animate-spin" />
								) : (
									<Download className="w-4 h-4" />
								)}
								<span className="hidden sm:inline">{translate('admin.staff.exportData')}</span>
							</Button>
							<Button
								size="sm"
								className="gap-2 bg-primary hover:bg-primary/90"
								onClick={() => setInviteModalOpen(true)}
							>
								<Send className="w-4 h-4" />
								{translate('admin.staff.inviteAllEmployees')}
							</Button>
						</div>
					}
				/>
			</motion.div>

			{/* Summary Stats */}
			<motion.section custom={1} variants={fadeUp}>
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
					<Card className="border-none shadow-xs">
						<CardContent className="p-4">
							<div className="flex items-center gap-2 mb-2">
								<Users className="w-5 h-5 text-primary" />
								<span className="text-sm text-muted-foreground">
									{translate('admin.staff.totalEmployees')}
								</span>
							</div>
							<p className="text-2xl font-bold">{total}</p>
							<p className="text-xs text-muted-foreground">
								{adoptionRate}% {translate('admin.staff.adoption')}
							</p>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs">
						<CardContent className="p-4">
							<div className="flex items-center gap-2 mb-2">
								<UserCheck className="w-5 h-5 text-primary" />
								<span className="text-sm text-muted-foreground">
									{translate('admin.staff.activeLabel')}
								</span>
							</div>
							<p className="text-2xl font-bold text-primary">{active}</p>
							<p className="text-xs text-muted-foreground">
								{translate('admin.staff.contributingToPlan')}
							</p>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs">
						<CardContent className="p-4">
							<div className="flex items-center gap-2 mb-2">
								<Clock className="w-5 h-5 text-amber-500" />
								<span className="text-sm text-muted-foreground">
									{translate('admin.staff.pendingLabel')}
								</span>
							</div>
							<p className="text-2xl font-bold text-amber-500">{pending}</p>
							<p className="text-xs text-muted-foreground">
								{translate('admin.staff.awaitingResponse')}
							</p>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs">
						<CardContent className="p-4">
							<div className="flex items-center gap-2 mb-2">
								<UserX className="w-5 h-5 text-secondary" />
								<span className="text-sm text-muted-foreground">
									{translate('admin.staff.declinedLabel')}
								</span>
							</div>
							<p className="text-2xl font-bold text-secondary">{declined}</p>
							<p className="text-xs text-muted-foreground">
								{translate('admin.staff.couldReconsider')}
							</p>
						</CardContent>
					</Card>
				</div>
			</motion.section>

			{/* Re-invite CTA */}
			{pending + declined > 0 && (
				<motion.section custom={2} variants={fadeUp}>
					<Card className="border-none shadow-xs bg-secondary/5">
						<CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
									<RotateCcw className="w-5 h-5 text-secondary" />
								</div>
								<div>
									<p className="text-sm font-semibold text-foreground">
										{pending + declined} {translate('admin.staff.notJoined')}
									</p>
									<p className="text-xs text-muted-foreground">
										{translate('admin.staff.notJoinedDescription')}
									</p>
								</div>
							</div>
							<Button
								size="sm"
								className="bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2 whitespace-nowrap"
								onClick={handleBulkReinvite}
							>
								<Mail className="w-4 h-4" />
								{translate('admin.staff.reinviteAll')} ({pending + declined})
							</Button>
						</CardContent>
					</Card>
				</motion.section>
			)}

			{/* Search & Filter bar */}
			<motion.div
				custom={3}
				variants={fadeUp}
				className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
			>
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
					<Input
						placeholder={translate('admin.staff.searchPlaceholder')}
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setCurrentPage(1);
						}}
						className="pl-9"
					/>
				</div>
				<Select
					value={statusFilter}
					onValueChange={(v) => {
						setStatusFilter(v as typeof statusFilter);
						setCurrentPage(1);
					}}
				>
					<SelectTrigger className="w-full sm:w-[180px]">
						<SelectValue placeholder={translate('admin.staff.filterByStatus')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{translate('admin.staff.allUsers')}</SelectItem>
						<SelectItem value="active">{translate('admin.staff.activeLabel')}</SelectItem>
						<SelectItem value="pending">{translate('admin.staff.pendingLabel')}</SelectItem>
						<SelectItem value="declined">{translate('admin.staff.declinedLabel')}</SelectItem>
						<SelectItem value="inactive">{translate('admin.staff.inactiveLabel')}</SelectItem>
					</SelectContent>
				</Select>
				<Select
					value={salaryFilter}
					onValueChange={(v) => {
						setSalaryFilter(v as typeof salaryFilter);
						setCurrentPage(1);
					}}
				>
					<SelectTrigger className="w-full sm:w-[180px]">
						<SelectValue placeholder={translate('admin.staff.salaryPlaceholder')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{translate('admin.staff.allSalaries')}</SelectItem>
						<SelectItem value="informed">{translate('admin.staff.salaryInformed')}</SelectItem>
						<SelectItem value="missing">{translate('admin.staff.salaryMissing')}</SelectItem>
					</SelectContent>
				</Select>
				{isHolding && allCompanies.length > 1 && (
					<Select
						value={companyFilter}
						onValueChange={(v) => {
							setCompanyFilter(v);
							setCurrentPage(1);
						}}
					>
						<SelectTrigger className="w-full sm:w-[200px]">
							<div className="flex items-center gap-2">
								<Building2 className="w-4 h-4 text-primary shrink-0" />
								<SelectValue placeholder={translate('admin.staff.filterByCompany')} />
							</div>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{translate('admin.staff.allCompanies')}</SelectItem>
							{allCompanies.map((c) => (
								<SelectItem key={c.id} value={c.id}>
									{c.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</motion.div>

			{/* Employee Table */}
			<motion.div custom={4} variants={fadeUp}>
				<Card className="shadow-xs overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="bg-muted/50">
								<tr className="text-left text-sm">
									<th className="px-4 py-3 font-medium">
										{translate('admin.staff.tableEmployee')}
									</th>
									{isHolding && allCompanies.length > 1 && (
										<th className="px-4 py-3 font-medium">
											{translate('admin.staff.tableCompany')}
										</th>
									)}
									<th className="px-4 py-3 font-medium text-center">
										{translate('admin.staff.tableStatus')}
									</th>
									<th className="px-4 py-3 font-medium text-center">
										{translate('admin.staff.tableSalary')}
									</th>
									<th className="px-4 py-3 font-medium text-right">
										{translate('admin.staff.tableContributionMonth')}
									</th>
									<th className="px-4 py-3 font-medium text-right">
										{translate('admin.staff.tableCompanyMonth')}
									</th>
									<th className="px-4 py-3 font-medium">{translate('admin.staff.tableSince')}</th>
									<th className="px-4 py-3 font-medium text-center">
										{translate('admin.staff.tableActions')}
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.length === 0 ? (
									<tr>
										<td
											colSpan={isHolding && allCompanies.length > 1 ? 8 : 7}
											className="px-4 py-8 text-center text-muted-foreground"
										>
											{translate('admin.staff.noEmployeesFound')}
										</td>
									</tr>
								) : (
									paginatedItems.map((emp) => (
										<tr
											key={emp.id}
											className="hover:bg-muted/30 transition-colors border-t border-border"
										>
											<td className="px-4 py-3">
												<div className="min-w-0">
													<p className="font-medium text-foreground truncate max-w-[200px]">
														{emp.name}
													</p>
													<p className="text-xs text-muted-foreground truncate max-w-[200px]">
														{emp.email}
													</p>
												</div>
											</td>
											{isHolding && allCompanies.length > 1 && (
												<td className="px-4 py-3">
													{emp.companyId && getCompanyName(emp.companyId) ? (
														<Badge
															variant="outline"
															className="bg-muted/50 text-muted-foreground border-border text-xs font-normal gap-1"
														>
															<Building2 className="w-3 h-3 text-primary" />
															<span className="truncate max-w-[120px]">
																{getCompanyName(emp.companyId)}
															</span>
														</Badge>
													) : (
														<span className="text-muted-foreground text-xs">—</span>
													)}
												</td>
											)}
											<td className="px-4 py-3 text-center">
												<Badge variant="outline" className={STATUS_CONFIG[emp.status].className}>
													{translate(STATUS_CONFIG[emp.status].labelKey)}
												</Badge>
											</td>
											<td className="px-4 py-3 text-center">
												{emp.status === 'active' ? (
													emp.salary != null ? (
														<CheckCircle2 className="w-4 h-4 text-primary mx-auto" />
													) : (
														<XCircle className="w-4 h-4 text-muted-foreground/40 mx-auto" />
													)
												) : (
													<span className="text-muted-foreground">—</span>
												)}
											</td>
											<td className="px-4 py-3 text-right font-medium tabular-nums">
												{emp.status === 'active'
													? `${formatArcaNumber(emp.monthlyContribution)} €`
													: '—'}
											</td>
											<td className="px-4 py-3 text-right font-medium tabular-nums">
												{emp.status === 'active'
													? `${formatArcaNumber(emp.companyContribution)} €`
													: '—'}
											</td>
											<td className="px-4 py-3 text-sm text-muted-foreground">
												{emp.status === 'active' ? (
													formatDate(emp.joinDate)
												) : (
													<span className="text-xs">
														{translate('admin.staff.invited')} {formatDate(emp.lastInvitedAt)}
													</span>
												)}
											</td>
											<td className="px-4 py-3 text-center">{renderActionMenu(emp)}</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					{/* Table footer */}
					<div className="px-4 py-3 border-t border-border bg-muted/30 flex flex-col sm:flex-row items-center justify-between gap-3">
						<p className="text-xs text-muted-foreground">
							{translate('admin.staff.showing')}{' '}
							{filtered.length > 0 ? (safePage - 1) * ITEMS_PER_PAGE + 1 : 0}–
							{Math.min(safePage * ITEMS_PER_PAGE, filtered.length)}{' '}
							{translate('admin.contributions.of')} {filtered.length}{' '}
							{translate('admin.staff.ofEmployees')}
						</p>

						{totalPages > 1 && (
							<div className="flex items-center gap-1">
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8"
									disabled={safePage <= 1}
									onClick={() => setCurrentPage(safePage - 1)}
								>
									<ChevronLeft className="w-4 h-4" />
								</Button>
								{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
									<Button
										key={page}
										variant={page === safePage ? 'default' : 'ghost'}
										size="icon"
										className="h-8 w-8 text-xs"
										onClick={() => setCurrentPage(page)}
									>
										{page}
									</Button>
								))}
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8"
									disabled={safePage >= totalPages}
									onClick={() => setCurrentPage(safePage + 1)}
								>
									<ChevronRight className="w-4 h-4" />
								</Button>
							</div>
						)}

						<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<TrendingUp className="w-3.5 h-3.5 text-primary" />
							{translate('admin.staff.totalContributionMonth')}{' '}
							<span className="font-semibold text-primary">
								{formatArcaNumber(
									employees
										.filter((e) => e.status === 'active')
										.reduce((s, e) => s + e.monthlyContribution + e.companyContribution, 0)
								)}{' '}
								€
							</span>
						</div>
					</div>
				</Card>
			</motion.div>

			{/* Invite Modal - V2 version */}
			<InviteEmployeesMinimalModalV2
				isOpen={inviteModalOpen}
				onClose={() => setInviteModalOpen(false)}
				onInvite={handleInvite}
				requirements={inviteRequirements}
				collectiveAgreementGroups={collectiveAgreementGroups}
				isLoading={isInviting}
				isLoadingRequirements={inviteRequirementsLoading}
				companySelector={
					isMultiCompany && allCompanies.length > 1 ? (
						<div className="space-y-1.5">
							<Label className="text-sm">{translate('admin.staff.selectCompanyToInvite')}</Label>
							<Select value={selectedInviteCompanyId} onValueChange={setSelectedInviteCompanyId}>
								<SelectTrigger>
									<SelectValue placeholder={translate('general.company.selectCompany')} />
								</SelectTrigger>
								<SelectContent>
									{allCompanies.map((company) => (
										<SelectItem key={company.id} value={company.id}>
											{company.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : undefined
				}
			/>

			{/* Bulk Salary Update Modal - V2 version */}
			<BulkSalaryReviewModalV2
				isOpen={bulkSalaryOpen}
				onClose={() => setBulkSalaryOpen(false)}
				token={token}
				companyId={companyId}
				userId={userId}
				onSuccess={fetchEmployees}
				translate={translate}
			/>

			{/* Individual Salary Edit Modal - V2 version */}
			{salaryDetailEmployee && (
				<EditSalaryModalV2
					isOpen={!!salaryDetailEmployee}
					onClose={() => setSalaryDetailEmployee(null)}
					user={{
						id: salaryDetailEmployee.id,
						email: salaryDetailEmployee.email,
						grossAnnualSalary: salaryDetailEmployee.salary || 0
					}}
					token={token}
					onSuccess={() => {
						setSalaryDetailEmployee(null);
						fetchEmployees();
					}}
					translate={translate}
				/>
			)}

			{/* Employee Leave Modal - V2 version */}
			{terminationEmployee && (
				<EmployeeLeaveModalV2
					isOpen={!!terminationEmployee}
					onClose={() => setTerminationEmployee(null)}
					onSuccess={() => {
						setTerminationEmployee(null);
						fetchEmployees();
					}}
					user={{
						id: terminationEmployee.id,
						name: terminationEmployee.name,
						email: terminationEmployee.email,
						// El modal los necesita para saber si tiene que pedir el contacto
						// personal antes de dejar confirmar la baja.
						personalEmail: terminationEmployee.personalEmail,
						phone: terminationEmployee.phone
					}}
					translate={translate}
				/>
			)}
		</motion.div>
	);
};

export default PlantillaV2;
